/**
 * Performance monitoring utilities for canvas rendering
 * Use in development to track render times and identify bottlenecks
 */

interface PerformanceMetrics {
    name: string;
    startTime: number;
    endTime?: number;
    duration?: number;
}

class PerformanceMonitor {
    private metrics: Map<string, PerformanceMetrics> = new Map();
    private enabled: boolean = process.env.NODE_ENV === 'development';

    start(name: string) {
        if (!this.enabled) return;
        this.metrics.set(name, {
            name,
            startTime: performance.now(),
        });
    }

    end(name: string) {
        if (!this.enabled) return;
        const metric = this.metrics.get(name);
        if (!metric) return;

        metric.endTime = performance.now();
        metric.duration = metric.endTime - metric.startTime;

        // Log slow operations (>50ms)
        if (metric.duration > 50) {
            console.warn(`[Performance] ${name} took ${metric.duration.toFixed(2)}ms`);
        }
    }

    measure<T>(name: string, fn: () => T): T {
        this.start(name);
        try {
            return fn();
        } finally {
            this.end(name);
        }
    }

    async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
        this.start(name);
        try {
            return await fn();
        } finally {
            this.end(name);
        }
    }

    getMetrics() {
        return Array.from(this.metrics.values())
            .filter(m => m.duration !== undefined)
            .sort((a, b) => (b.duration || 0) - (a.duration || 0));
    }

    clear() {
        this.metrics.clear();
    }

    report() {
        if (!this.enabled) return;
        const metrics = this.getMetrics();
        if (metrics.length === 0) return;

        console.group('Performance Report');
        metrics.forEach(m => {
            console.log(`${m.name}: ${m.duration?.toFixed(2)}ms`);
        });
        console.groupEnd();
    }

    enable() {
        this.enabled = true;
    }

    disable() {
        this.enabled = false;
    }
}

export const perfMonitor = new PerformanceMonitor();

// Expose to window for debugging
if (typeof window !== 'undefined') {
    (window as any).perfMonitor = perfMonitor;
}
